
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Injector,
  input,
  OnInit,
  output,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl, Validators } from '@angular/forms';
import { OptgroupComponent } from '../optgroup/optgroup.component';
import { OptionComponent } from '../option/option.component';

export interface MultiselectItem<T> {
  id: string | number;
  value: T;
  label: string;
  disabled?: boolean;
}

export interface MultiselectOptgroup<T> {
  label: string;
  options: MultiselectItem<T>[];
}

@Component({
  selector: 'ui-multiselect',
  imports: [],
  templateUrl: './multiselect.component.html',
  styleUrls: ['../../_styles/index.scss', '../selects.scss', './multiselect.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => MultiselectComponent), multi: true }],
})
export class MultiselectComponent<T> implements ControlValueAccessor, OnInit {
  // Injections
  private readonly elementRef = inject(ElementRef);
  private readonly injector = inject(Injector);
  private ngControl: NgControl | null = null;

  // View children
  readonly uiOptgroups = contentChildren(OptgroupComponent);
  readonly uiOptions = contentChildren(OptionComponent, { descendants: true });
  readonly triggerButton = viewChild<ElementRef<HTMLButtonElement>>('triggerButton');
  readonly dropdown = viewChild<ElementRef<HTMLDivElement>>('dropdown');
  readonly projectedContentWrapper = viewChild<ElementRef>('projectedContentWrapper');

  // Inputs
  readonly label = input.required<string>();
  readonly options = input<MultiselectItem<T>[]>();
  readonly optgroups = input<MultiselectOptgroup<T>[]>();
  readonly emptyMultiselectMessage = input<string>('No results found');
  readonly placeholder = input<string>('Select options');
  readonly labelPosition = input<'top' | 'left' | 'right' | 'hide'>('top');
  readonly size = input<'default' | 'compact'>('default');
  readonly showCheckbox = input<boolean>(false);
  readonly showSelectAll = input<boolean>(false);
  readonly showUnselectAll = input<boolean>(false);
  readonly showSearch = input<boolean>(false);
  readonly filterResultsThroughSearchTerm = input<boolean>(true);
  readonly searchPlaceholder = input<string>('Type to filter options');

  // Outputs
  searchTermChanged = output<string>();


  // Generated attributes
  readonly id = crypto.randomUUID();
  // Accessibility: Unique ID for semantic linking via aria-controls
  readonly dropdownId = `${this.id}-dropdown`;
  // Accessibility: Live region ID for announcing selection changes to screen readers
  readonly liveRegionId = `${this.id}-live`;
  // Accessibility: Instructions ID for describing multiselect behavior to screen readers
  readonly instructionsId = `${this.id}-instructions`;

  // State
  readonly selectedValues = signal<MultiselectItem<T>[]>([]);
  readonly isOpen = signal(false);
  // Filter term for searching/filtering options in dropdown
  readonly searchTerm = signal('');
  // Accessibility: Tracks focused option index for keyboard navigation
  readonly focusedIndex = signal(-1);
  // Accessibility: Message announced to screen readers via live region
  readonly liveMessage = signal('');
  protected readonly disabled = signal(false);

  // Keyboard constants
  private readonly KEYS = {
    SPACE: ' ',
    ESCAPE: 'Escape',
    ENTER: 'Enter',
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
  } as const;

  // Reactively parse content children when they change
  readonly parsedOptgroups = computed(() => {
    if (this.options() || this.optgroups()) return [];

    const uiOptgroups = this.uiOptgroups();
    if (uiOptgroups.length) {
      return uiOptgroups.map(group => ({
        label: group.label(),
        options: group
          .uiOptions()
          .filter(opt => opt.value() !== undefined)
          .map(opt => ({
            id: crypto.randomUUID(),
            value: opt.value() as T,
            label: opt.elementRef.nativeElement.textContent?.trim() || '',
            disabled: opt.disabled(),
          })),
      }));
    }

    const uiOpts = this.uiOptions();
    if (uiOpts.length) {
      const options = uiOpts
        .filter(opt => opt.value() !== undefined)
        .map(opt => ({
          id: crypto.randomUUID(),
          value: opt.value() as T,
          label: opt.elementRef.nativeElement.textContent?.trim() || '',
          disabled: opt.disabled(),
        }));
      return [{ label: '', options }];
    }

    return [];
  });

  // Computed values
  private readonly allOptions = computed(() => {
    if (this.optgroups()) return this.optgroups()!.flatMap(g => g.options);
    if (this.parsedOptgroups().length) return this.parsedOptgroups().flatMap(g => g.options);
    return this.options() || [];
  });

  private readonly selectedValuesSet = computed(() => new Set(this.selectedValues().map(value => value.id)));

  private readonly enabledOptions = computed(() => this.allOptions().filter(option => !option.disabled));

  // Accessibility: Compute ID of currently focused option for aria-activedescendant
  readonly activeDescendantId = computed(() => {
    const index = this.focusedIndex();
    if (index < 0 || !this.isOpen()) return '';
    const options = this.enabledOptions();
    return options[index]?.id ? `option-${options[index].id}` : '';
  });

  readonly ariaLabel = computed(() => {
    const count = this.selectedValues().length;
    if (count === 0) return `${this.label()}, ${this.placeholder()}`;
    if (count === 1) return `${this.label()}, ${this.selectedValues()[0].label} selected`;
    return `${this.label()}, ${count} items selected`;
  });

  readonly selectedLabels = computed(() => {
    const selected = this.selectedValues();
    if (!selected.length) return this.placeholder();
    return selected.map(val => val.label).join(', ');
  });

  readonly isRequired = computed(() => this.ngControl?.control?.hasValidator(Validators.required) ?? false);

  readonly displayOptgroups = computed(() => (this.optgroups() || this.parsedOptgroups()).filter(g => g.label));

  readonly displayOptions = computed(() => {
    if (this.options()) return this.options()!;
    const groups = this.parsedOptgroups();
    return groups.length === 1 && !groups[0].label ? groups[0].options : [];
  });

  // Filter flat options by search term
  readonly filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term || !this.filterResultsThroughSearchTerm()) return this.displayOptions();
    return this.displayOptions().filter(opt => opt.label.toLowerCase().includes(term));
  });

  // Filter grouped options by search term and remove empty groups
  readonly filteredOptgroups = computed(() => {
    const term = this.searchTerm().toLowerCase();
    if (!term || !this.filterResultsThroughSearchTerm()) return this.displayOptgroups();
    return this.displayOptgroups()
      .map(group => ({
        ...group,
        options: group.options.filter(opt => opt.label.toLowerCase().includes(term)),
      }))
      .filter(group => group.options.length > 0);
  });

  readonly hasProjectedContent = computed(() => {
    const wrapper = this.projectedContentWrapper();
    return wrapper ? wrapper.nativeElement?.children?.length > 0 : false;
  });

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null, { self: true, optional: true });
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  // ControlValueAccessor implementation
  private onChange = (_: MultiselectItem<T>[]) => {};
  private onTouched = () => {};

  writeValue(value: MultiselectItem<T>[]): void {
    this.selectedValues.set(value || []);
  }

  registerOnChange(fn: (value: MultiselectItem<T>[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Dropdown and selection handlers
  // Open dropdown with initial focus
  private openDropdown(): void {
    this.isOpen.set(true);
    this.focusedIndex.set(0);
    setTimeout(() => this.focusDropdownItem(0));
  }

  // Open or close the dropdown menu
  toggleDropdown(): void {
    if (!this.disabled()) {
      const willOpen = !this.isOpen();
      this.isOpen.set(willOpen);
      if (willOpen) {
        this.openDropdown();
      }
    }
  }

  // Accessibility: Close dropdown and return focus to trigger button
  closeDropdown(): void {
    this.isOpen.set(false);
    this.focusedIndex.set(-1);
    // Reset search filter when closing
    this.searchTerm.set('');
    this.triggerButton()?.nativeElement.focus();
  }

  // Accessibility: Move focus to dropdown option at specified index
  private focusDropdownItem(index: number): void {
    const items = this.dropdown()?.nativeElement.querySelectorAll('.option:not(.disabled)');
    if (items && items[index]) {
      (items[index] as HTMLElement).focus();
    }
  }

  // Add or remove a single option from selection
  toggleOption(value: MultiselectItem<T>): void {
    if (value.disabled || this.disabled()) return;

    const values = [...this.selectedValues()];
    const index = values.findIndex(v => v.id === value.id);

    if (index > -1) {
      values.splice(index, 1);
      // Accessibility: Announce deselection and total count to screen readers
      this.liveMessage.set(`${value.label} deselected, ${values.length} items selected`);
    } else {
      values.push(value);
      // Accessibility: Announce selection and total count to screen readers
      this.liveMessage.set(`${value.label} selected, ${values.length} items selected`);
    }

    this.updateValue(values);
  }

  // Select or deselect all enabled options in an optgroup
  toggleOptgroup(optgroup: MultiselectOptgroup<T>): void {
    const selectedSet = this.selectedValuesSet();
    const enabledValues = this.getEnabledValues(optgroup);
    const allSelected = enabledValues.every(val => selectedSet.has(val.id));

    const values = [...this.selectedValues()];

    if (allSelected) {
      const enabledIds = new Set(enabledValues.map(v => v.id));
      const filtered = values.filter(v => !enabledIds.has(v.id));
      this.updateValue(filtered);
      this.liveMessage.set(`${optgroup.label} deselected, ${filtered.length} items selected`);
    } else {
      const toAdd = enabledValues.filter(val => !selectedSet.has(val.id));
      const updated = [...values, ...toAdd];
      this.updateValue(updated);
      this.liveMessage.set(`${optgroup.label} selected, ${updated.length} items selected`);
    }
  }

  // Update selected values and notify form control
  private updateValue(values: MultiselectItem<T>[]): void {
    this.selectedValues.set(values);
    this.onChange(values);
  }

  // Display helpers
  // Check if a specific value is selected
  isSelected(value: MultiselectItem<T>): boolean {
    return this.selectedValuesSet().has(value.id);
  }

  // Check if all enabled options in an optgroup are selected
  isOptgroupAllSelected(optgroup: MultiselectOptgroup<T>): boolean {
    const enabledValues = this.getEnabledValues(optgroup);
    const selectedSet = this.selectedValuesSet();
    return enabledValues.length > 0 && enabledValues.every(val => selectedSet.has(val.id));
  }

  // Check if some but not all options in an optgroup are selected
  isOptgroupIndeterminate(optgroup: MultiselectOptgroup<T>): boolean {
    const enabledValues = this.getEnabledValues(optgroup);
    const selectedSet = this.selectedValuesSet();
    const selectedCount = enabledValues.filter(val => selectedSet.has(val.id)).length;
    return selectedCount > 0 && selectedCount < enabledValues.length;
  }

  // Get non-disabled option values from an optgroup
  private getEnabledValues(optgroup: MultiselectOptgroup<T>): MultiselectItem<T>[] {
    return optgroup.options.filter(opt => !opt.disabled);
  }

  // Select all non-disabled options
  selectAll(): void {
    const allEnabled = this.enabledOptions();
    this.updateValue(allEnabled);
    this.liveMessage.set(`All options selected, ${allEnabled.length} items selected`);
  }

  // Unselect all options
  unselectAll(): void {
    this.updateValue([]);
    this.liveMessage.set('All options deselected');
  }

  // Update search term from input field
  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
    this.searchTermChanged.emit(this.searchTerm());
  }

  // Event handlers
  handleOnBlur(): void {
    this.onTouched();
  }

  // Accessibility: Navigate through options with arrow keys
  private navigateOptions(direction: 'up' | 'down', items: NodeListOf<Element>): void {
    const current = this.focusedIndex();
    const next = direction === 'down' ? Math.min(current + 1, items.length - 1) : Math.max(current - 1, 0);
    this.focusedIndex.set(next);
    this.focusDropdownItem(next);
    this.announceOption(next, items.length);
  }

  // Accessibility: Handle keyboard interaction on trigger button (Space, Arrow keys open dropdown)
  handleTriggerKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;

    if (event.key === this.KEYS.SPACE || event.key === this.KEYS.ARROW_DOWN || event.key === this.KEYS.ARROW_UP) {
      event.preventDefault();
      if (!this.isOpen()) {
        this.openDropdown();
      } else if (event.key === this.KEYS.SPACE) {
        this.closeDropdown();
      }
    }
  }

  // Accessibility: Handle keyboard navigation in dropdown (Escape closes, Enter/Space toggles, Arrows navigate)
  handleDropdownKeydown(event: KeyboardEvent, optionValue?: MultiselectItem<T>): void {
    if (event.key === this.KEYS.ESCAPE) {
      event.preventDefault();
      this.closeDropdown();
      return;
    }

    if (!optionValue) return;

    const items = this.dropdown()?.nativeElement.querySelectorAll('.option:not(.disabled)');

    if (event.key === this.KEYS.ENTER || event.key === this.KEYS.SPACE) {
      event.preventDefault();
      this.toggleOption(optionValue);
    } else if (event.key === this.KEYS.ARROW_DOWN) {
      event.preventDefault();
      this.navigateOptions('down', items!);
    } else if (event.key === this.KEYS.ARROW_UP) {
      event.preventDefault();
      this.navigateOptions('up', items!);
    }
  }

  // Accessibility: Handle keyboard interaction on optgroup headers
  handleOptgroupKeydown(event: KeyboardEvent, optgroup: MultiselectOptgroup<T>): void {
    if (event.key === this.KEYS.ENTER || event.key === this.KEYS.SPACE) {
      event.preventDefault();
      this.toggleOptgroup(optgroup);
    }
  }

  // Accessibility: Announce option label, selection state, and position (e.g., "Coco Pops, selected, 2 of 5")
  private announceOption(index: number, total: number): void {
    const options = this.enabledOptions();
    const option = options[index];
    if (option) {
      const status = this.isSelected(option) ? 'selected' : 'not selected';
      this.liveMessage.set(`${option.label}, ${status}, ${index + 1} of ${total}`);
    }
  }

  // Accessibility: Close dropdown when clicking outside component
  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent): void {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.closeDropdown();
    }
  }
}
